import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Chip,
  Paper,
  CircularProgress,
  GlobalStyles,
} from '@mui/material';
import { GridLegacy as Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  SalesReportRequest,
  SalesReportRequestDateRangeEnum,
  SalesReportRequestPaymentMethodsEnum,
  SalesReportRequestOrderStatusesEnum,
} from '../../../client-sdk/api';
import { useDownloadSalesReport } from '../../Query/ReportsQuery';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useSnackbar } from '../../stateless/SnackBarProvider';

type StoreOption = {
  value: string;
  label: string;
};

type RawStore = {
  id: string;
  nameEnglish?: string;
  restaurantDetails?: {
    storeName?: string;
  };
};

const SalesReport: React.FC = () => {
  const { t } = useTranslation();
  const { mutate: downloadReport, isPending: isDownloading } = useDownloadSalesReport();
  const { showMessage } = useSnackbar();

  // Get logged-in user's merchant ID
  const loggedInMerchantId = useMemo(() => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return userData?.userId as string | undefined;
  }, []);

  // Filter states
  const [reportType] = useState<'daily' | 'weekly' | 'monthly' | 'custom'>('custom');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs().startOf('day'));
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs().endOf('day'));
  const [selectedOrderStatuses, setSelectedOrderStatuses] = useState<string[]>([]);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);

  const isDateToAfterToday = useMemo(
    () => (dateTo ? dateTo.isAfter(dayjs().endOf('day')) : false),
    [dateTo]
  );

  // Fetch stores & owners for filters
  const storeQueryParams = useMemo(
    () => ({
      sortBy: 'createdAt',
      sortOrder: 'asc' as const,
      page: 1,
      limit: 100,
      addedByFilter: 'All',
      statusFilter: 'All',
      search: undefined,
    }),
    []
  );

  const { data: storesData } = useAllStores(storeQueryParams);

  const storeOptions: StoreOption[] = useMemo(() => {
    const rawStores = (storesData as { data?: RawStore[] } | undefined)?.data;
    if (!rawStores) return [];
    return rawStores.map((store) => ({
      value: store.id,
      label: store.nameEnglish || store.restaurantDetails?.storeName || '',
    }));
  }, [storesData]);

  // Update initial date range based on report type
  useEffect(() => {
    const today = dayjs();
    switch (reportType) {
      case 'daily':
        setDateFrom(today.startOf('day'));
        setDateTo(today.endOf('day'));
        break;
      case 'weekly': {
        const start = today.startOf('day');
        const todayEnd = today.endOf('day');
        const tentativeEnd = start.add(6, 'day').endOf('day');
        const end = tentativeEnd.isAfter(todayEnd) ? todayEnd : tentativeEnd;
        setDateFrom(start);
        setDateTo(end);
        break;
      }
      case 'monthly':
        setDateFrom(today.startOf('month'));
        setDateTo(today.endOf('month'));
        break;
      case 'custom':
        // Keep current dates
        break;
    }
  }, [reportType]);

  // When report type is weekly, auto-set "to" date = "from" + 6 days
  useEffect(() => {
    if (reportType === 'weekly' && dateFrom) {
      const start = dateFrom.startOf('day');
      const todayEnd = dayjs().endOf('day');
      const tentativeEnd = start.add(6, 'day').endOf('day');
      const end = tentativeEnd.isAfter(todayEnd) ? todayEnd : tentativeEnd;
      setDateTo(end);
    }
  }, [reportType, dateFrom]);

  // When report type is daily, keep "to" date in sync with the chosen single day
  useEffect(() => {
    if (reportType === 'daily' && dateFrom) {
      setDateTo(dateFrom.endOf('day'));
    }
  }, [reportType, dateFrom, setDateTo]);

  const handleDownload = () => {
    // Map report type to API dateRange and compute dateFrom/dateTo
    const from = dateFrom ?? dayjs();
    const to = dateTo ?? dateFrom ?? dayjs();

    // Basic front-end validation: prevent future "to" dates
    if (to.isAfter(dayjs().endOf('day'))) {
      showMessage('Date To cannot be greater than today', 'error');
      return;
    }

    let dateRange: SalesReportRequestDateRangeEnum;
    let dateFromStr: string | undefined;
    let dateToStr: string | undefined;

    if (reportType === 'daily') {
      const target = from;
      dateRange = SalesReportRequestDateRangeEnum.Today;
      dateFromStr = target.startOf('day').toISOString();
      dateToStr = target.endOf('day').toISOString();
    } else if (reportType === 'weekly') {
      // For weekly, treat selected "from" date as start and automatically add 6 days, capped at today
      const start = from.startOf('day');
      const todayEnd = dayjs().endOf('day');
      const tentativeEnd = start.add(6, 'day').endOf('day');
      const end = tentativeEnd.isAfter(todayEnd) ? todayEnd : tentativeEnd;
      dateRange = SalesReportRequestDateRangeEnum.Custom;
      dateFromStr = start.toISOString();
      dateToStr = end.toISOString();
    } else if (reportType === 'monthly') {
      const base = from;
      const start = base.startOf('month');
      const end = base.endOf('month');
      dateRange = SalesReportRequestDateRangeEnum.ThisMonth;
      dateFromStr = start.toISOString();
      dateToStr = end.toISOString();
    } else {
      // custom arbitrary range
      dateRange = SalesReportRequestDateRangeEnum.Custom;
      dateFromStr = from.startOf('day').toISOString();
      dateToStr = to.endOf('day').toISOString();
    }

    // Build request
    const request: SalesReportRequest = {
      dateRange,
      dateFrom: dateFromStr,
      dateTo: dateToStr,
      merchantId: loggedInMerchantId,
      storeIds: selectedStores.length > 0 ? selectedStores : undefined,
      orderStatuses:
        selectedOrderStatuses.length > 0
          ? (selectedOrderStatuses as SalesReportRequestOrderStatusesEnum[])
          : undefined,
      paymentMethods:
        selectedPaymentMethods.length > 0
          ? (selectedPaymentMethods as SalesReportRequestPaymentMethodsEnum[])
          : undefined,
    };

    downloadReport(request);
  };

  const orderStatusOptions = [
    { value: 'ORDER_DELIVERED', label: t('reports.orderStatus.delivered') },
    { value: 'ORDER_CANCELLED', label: t('reports.orderStatus.cancelled') },
    { value: 'MERCHANT_CANCELLED', label: t('reports.orderStatus.merchantCancelled') },
    { value: 'DELIVERY_FAILED', label: t('reports.orderStatus.deliveryFailed') },
  ];

  const paymentMethodOptions = [
    { value: 'cashOnDelivery', label: t('reports.paymentMethod.cash') },
    { value: 'onlinePayment', label: t('reports.paymentMethod.online') },
    { value: 'wallet', label: t('reports.paymentMethod.wallet') },
    { value: 'walletAndCashOnDelivery', label: t('reports.paymentMethod.walletCash') },
    { value: 'walletAndOnlinePayment', label: t('reports.paymentMethod.walletOnline') },
  ];

  // Common input styling
  const commonInputSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: '40px !important',
      minHeight: '40px !important',
      display: 'flex',
      alignItems: 'center',
      '& .MuiSelect-select': {
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        paddingTop: 0,
        paddingBottom: 0,
        lineHeight: '40px',
      },
      // Ensure the autocomplete placeholder/text is vertically centered
      '& .MuiAutocomplete-input': {
        padding: '0 14px !important',
        height: '100% !important',
        minHeight: '0 !important',
        boxSizing: 'border-box',
      },
      '& .MuiInputBase-input': {
        padding: '0 14px !important',
        height: '100% !important',
        minHeight: '0 !important',
        boxSizing: 'border-box',
      },
      '& input': {
        padding: '0 14px !important',
        height: '100% !important',
        minHeight: '0 !important',
        boxSizing: 'border-box',
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <GlobalStyles
        styles={{
          '.MuiAutocomplete-popper': {
            zIndex: '1300 !important',
          },
        }}
      />
      <Box sx={{ 
        py: 3,
        px: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Typography 
            variant="h4" 
            fontWeight="bold" 
            mb={3} 
            sx={{ 
              color: 'text.primary',
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            {t('reports.sales.title')}
          </Typography>

          <Paper 
            elevation={1}
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              mb: 3,
            }}
          >
            <Grid container spacing={3}>
              {/* Date From */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {t('reports.dateFrom')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <DatePicker
                      value={dateFrom}
                      onChange={(newValue) => {
                        if (!newValue) {
                          setDateFrom(null);
                          if (reportType === 'monthly') {
                            setDateTo(null);
                          }
                          return;
                        }

                        if (reportType === 'monthly') {
                          const base = newValue.startOf('month');
                          setDateFrom(base);
                          setDateTo(base.endOf('month'));
                        } else {
                          setDateFrom(newValue);
                        }
                      }}
                      maxDate={dayjs()}
                      disabled={false}
                      views={
                        reportType === 'monthly'
                          ? ['year', 'month']
                          : ['year', 'month', 'day']
                      }
                      format={
                        reportType === 'monthly' ? 'MMMM YYYY' : undefined
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: commonInputSx,
                          placeholder: t('reports.dateFrom'),
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Date To */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {t('reports.dateTo')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <DatePicker
                      value={dateTo}
                      onChange={(newValue) => setDateTo(newValue)}
                      disabled={reportType === 'daily' || reportType === 'monthly'}
                      maxDate={dayjs()}
                      minDate={reportType === 'custom' && dateFrom ? dateFrom : undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: isDateToAfterToday,
                          helperText: isDateToAfterToday
                            ? 'Date To cannot be greater than today'
                            : undefined,
                          sx: commonInputSx,
                          placeholder: t('reports.dateTo'),
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Stores */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontWeight: 500, pt: 1 }}>
                      {t('reports.stores')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Autocomplete
                      multiple
                      options={storeOptions}
                      getOptionLabel={(option) => option.label}
                      value={storeOptions.filter((opt) =>
                        selectedStores.includes(opt.value)
                      )}
                      onChange={(_, newValue) => {
                        setSelectedStores(newValue.map((v) => v.value));
                      }}
                      disableCloseOnSelect
                      openOnFocus
                      clearOnBlur={false}
                      disableClearable
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t('reports.selectStores')}
                          variant="outlined"
                          sx={commonInputSx}
                        />
                      )}
                      renderTags={() => null}
                      sx={{ width: '100%' }}
                    />
                    {selectedStores.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mt: 1.5,
                        }}
                      >
                        {storeOptions
                          .filter((opt) => selectedStores.includes(opt.value))
                          .map((option) => (
                            <Chip
                              key={option.value}
                              label={option.label}
                              onDelete={() => {
                                setSelectedStores((prev) =>
                                  prev.filter((val) => val !== option.value)
                                );
                              }}
                              size="small"
                            />
                          ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {/* Order Status */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontWeight: 500, pt: 1 }}>
                      {t('reports.orderStatus')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Autocomplete
                      multiple
                      options={orderStatusOptions}
                      getOptionLabel={(option) => option.label}
                      value={orderStatusOptions.filter((opt) =>
                        selectedOrderStatuses.includes(opt.value)
                      )}
                      onChange={(_, newValue) => {
                        setSelectedOrderStatuses(newValue.map((v) => v.value));
                      }}
                      disableCloseOnSelect
                      openOnFocus
                      clearOnBlur={false}
                      disableClearable
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t('reports.selectOrderStatuses')}
                          variant="outlined"
                          sx={commonInputSx}
                        />
                      )}
                      renderTags={() => null}
                      sx={{ width: '100%' }}
                    />
                    {selectedOrderStatuses.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mt: 1.5,
                        }}
                      >
                        {orderStatusOptions
                          .filter((opt) => selectedOrderStatuses.includes(opt.value))
                          .map((option) => (
                            <Chip
                              key={option.value}
                              label={option.label}
                              onDelete={() => {
                                setSelectedOrderStatuses(
                                  selectedOrderStatuses.filter((val) => val !== option.value)
                                );
                              }}
                              size="small"
                            />
                          ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {/* Payment Method */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontWeight: 500, pt: 1 }}>
                      {t('reports.paymentMethod')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Autocomplete
                      multiple
                      options={paymentMethodOptions}
                      getOptionLabel={(option) => option.label}
                      value={paymentMethodOptions.filter((opt) =>
                        selectedPaymentMethods.includes(opt.value)
                      )}
                      onChange={(_, newValue) => {
                        setSelectedPaymentMethods(newValue.map((v) => v.value));
                      }}
                      disableCloseOnSelect
                      openOnFocus
                      clearOnBlur={false}
                      disableClearable
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t('reports.selectPaymentMethods')}
                          variant="outlined"
                          sx={commonInputSx}
                        />
                      )}
                      renderTags={() => null}
                      sx={{ width: '100%' }}
                    />
                    {selectedPaymentMethods.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mt: 1.5,
                        }}
                      >
                        {paymentMethodOptions
                          .filter((opt) => selectedPaymentMethods.includes(opt.value))
                          .map((option) => (
                            <Chip
                              key={option.value}
                              label={option.label}
                              onDelete={() => {
                                setSelectedPaymentMethods(
                                  selectedPaymentMethods.filter((val) => val !== option.value)
                                );
                              }}
                              size="small"
                            />
                          ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {/* Download Report Button */}
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Button
                    variant="contained"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    size="large"
                    sx={{ 
                      minWidth: 200,
                      height: '40px',
                    }}
                  >
                    {isDownloading ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1.5, color: 'white' }} />
                        {t('reports.downloading')}
                      </>
                    ) : (
                      t('reports.download')
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Fields Info */}
          <Paper 
            elevation={1}
            sx={{ 
              p: { xs: 2, sm: 3 },
            }}
          >
            <Typography 
              variant="h6" 
              mb={2} 
              fontWeight={600}
              sx={{ 
                fontSize: '1.1rem',
              }}
            >
              {t('reports.sales.fieldsTitle')}
            </Typography>
            <Box>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  lineHeight: 1.8,
                  fontSize: '0.95rem',
                }}
              >
                {t('reports.sales.fieldsDescription')}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default SalesReport;

